'use client'

import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import { Home, Waves, Map, GraduationCap, User } from 'lucide-react'
import { useTheme } from '@/contexts/ThemeContext'
import { useEffect, useState } from 'react'
import { useGlobalUnreadStore } from '@/store/chatStore'

interface NavItem {
  id: string
  label: string
  icon: React.ReactNode
  path: string
  paths?: string[] // 해당 탭이 활성화되어야 하는 추가 경로들
}

export default function BottomNav() {
  const router = useRouter()
  const pathname = usePathname()
  const { theme } = useTheme()
  const [mounted, setMounted] = useState(false)

  // 전역 unread count store 사용 (Header와 공유)
  const { totalUnread: unreadCount, fetchUnreadCount } = useGlobalUnreadStore()

  useEffect(() => {
    setMounted(true)
  }, [])

  // BottomNav에서는 fetch하지 않음 - Header에서만 fetch하고 store를 공유
  // 다만 채팅 페이지 진입 시에만 갱신
  useEffect(() => {
    if (!mounted) return

    // 채팅 페이지에서 나올 때만 갱신
    if (!pathname.startsWith('/chat')) {
      fetchUnreadCount()
    }
  }, [mounted, pathname, fetchUnreadCount])

  const navItems: NavItem[] = [
    {
      id: 'home',
      label: '홈',
      icon: <Home className="w-6 h-6" />,
      path: '/',
      paths: ['/buddy-match', '/match-buddy', '/matching']
    },
    {
      id: 'tours',
      label: '투어',
      icon: <Waves className="w-6 h-6" />,
      path: '/tours',
      paths: ['/tours', '/reservations']
    },
    {
      id: 'map',
      label: '지도',
      icon: <Map className="w-6 h-6" />,
      path: '/map',
      paths: ['/map', '/valleys', '/spots', '/pools']
    },
    {
      id: 'instructor',
      label: '강사찾기',
      icon: <GraduationCap className="w-6 h-6" />,
      path: '/instructors',
      paths: ['/instructors', '/instructor']
    },
    {
      id: 'profile',
      label: '마이',
      icon: <User className="w-6 h-6" />,
      path: '/profile',
      paths: ['/profile', '/settings']
    }
  ]

  const isActive = (item: NavItem) => {
    if (pathname === item.path) return true
    if (item.paths) {
      return item.paths.some(p => pathname.startsWith(p))
    }
    return false
  }

  // 모든 네비게이션 경로 미리 prefetch (마운트 시)
  useEffect(() => {
    if (mounted) {
      navItems.forEach(item => {
        router.prefetch(item.path)
      })
    }
  }, [mounted, router])

  // 실제 높이를 CSS 변수로 설정
  useEffect(() => {
    if (!mounted) return
    const el = document.getElementById('bottom-nav')
    const update = () => {
      const h = el ? el.offsetHeight : 60
      document.documentElement.style.setProperty('--bottom-nav-height', `${h}px`)
    }
    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [mounted])

  if (!mounted) return null

  // 채팅방 상세 페이지에서는 BottomNav 숨김 (자체 입력창이 있음)
  const isChatRoom = pathname.match(/^\/chat\/[^/]+$/)
  if (isChatRoom) return null

  const isDark = theme === 'dark'

  const activeColor = isDark ? '#5ea6ff' : '#2b6df0'
  const activeBg = isDark ? 'rgba(94,166,255,0.12)' : 'rgba(43,109,240,0.08)'
  const inactiveColor = isDark ? '#64748b' : '#94a3b8'
  const navBg = isDark ? 'rgba(15,23,42,0.97)' : 'rgba(255,255,255,0.97)'
  const navBorder = isDark ? '#1e2a3d' : '#e9eff7'

  return (
    <nav
      id="bottom-nav"
      className="fixed bottom-0 left-0 right-0 z-50 safe-area-bottom backdrop-blur-xl"
      style={{ background: navBg, borderTop: `1px solid ${navBorder}` }}
    >
      <div className="max-w-lg mx-auto px-1">
        <div className="flex items-center justify-around py-1">
          {navItems.map((item) => {
            const active = isActive(item)
            return (
              <Link
                key={item.id}
                href={item.path}
                prefetch={true}
                className="flex flex-col items-center justify-center flex-1 py-1.5 px-1 min-h-[52px] transition-all duration-150"
                style={{ color: active ? activeColor : inactiveColor }}
              >
                <div
                  className="relative p-1.5 rounded-xl transition-all duration-150"
                  style={{ background: active ? activeBg : 'transparent' }}
                >
                  {item.icon}
                  {item.id === 'matching' && unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-red-500 rounded-full" />
                  )}
                </div>
                <span className="text-[11px] mt-0.5" style={{ fontWeight: active ? 600 : 500 }}>
                  {item.label}
                </span>
              </Link>
            )
          })}
        </div>
      </div>
    </nav>
  )
}
